import { useEffect, useState } from "react";
import { Building2, Camera, Check, FileDown, Map, MapPin, Server, ShieldAlert, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, rupiah } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RouterCombobox } from "../components/RouterCombobox";
import { FibiLoading } from "../components/FibiLoading";
import { BranchLeaderboard } from "../components/BranchLeaderboard";
import { SetupRouterDialog } from "../components/SetupRouterDialog";
import { DatePicker } from "../components/DatePicker";
import { MapPointPicker } from "../components/MapPointPicker";
import { MiniMap } from "../components/MiniMap";

const TYPES = ["Rumahan", "Voucheran", "Rumahan + Voucheran"];
const EMPTY = { name: "", wifi_type: "Rumahan + Voucheran", homes: 30, pln: "ya", competitors: "", competitor_price: "", target_date: "", network: "OLT", note: "", lat: "", lng: "" };
const STATUS = { pending: "bg-amber-500/15 text-amber-500 border-amber-500/30", approved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30", rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30" };

function ProfilList({ title, items, testid, kind, showBelum }) {
  const belum = showBelum ? <span className="text-[10px] font-mono text-amber-500 whitespace-nowrap">belum di router</span> : null;
  if (kind === "rumahan" || kind === "voucher-simple") {
    return (
      <div className="space-y-1" data-testid={testid}>
        <div className="text-xs font-semibold">{title}</div>
        <div className="border border-border rounded-xl divide-y divide-border bg-background/60">
          {items.map((p) => (
            <div key={p.profile} className="flex items-center gap-3 px-3 py-2" data-testid={`${testid}-${p.profile}`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-semibold truncate">{p.profile}{kind === "rumahan" && <span className="text-muted-foreground font-normal text-xs"> • {p.rate_limit || "-"} • {p.expired_days ?? 30} hari</span>}</div>
                <div className="text-xs font-mono flex gap-3"><span className="text-muted-foreground">modal {rupiah(p.modal)}</span><span className="text-emerald-500 font-bold">jual {rupiah(p.jual)}</span></div>
              </div>
              {belum}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="text-xs font-semibold">{title}</div>
      <div className="border border-border rounded-lg overflow-x-auto bg-background/60">
        <Table className="min-w-[640px] text-xs">
          <TableHeader><TableRow><TableHead>Nama Paket</TableHead><TableHead>Rate Limit</TableHead><TableHead>Validity</TableHead><TableHead>Time Limit</TableHead><TableHead>Data Limit</TableHead><TableHead>Harga Modal</TableHead><TableHead>Harga Jual</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.profile} data-testid={`${testid}-${p.profile}`}>
                <TableCell className="font-semibold">{p.profile}{belum && <div className="mt-0.5">{belum}</div>}</TableCell>
                <TableCell className="font-mono">{p.rate_limit || "-"}</TableCell>
                <TableCell className="font-mono">{p.validity || "-"}</TableCell>
                <TableCell className="font-mono">{p.time_limit || "-"}</TableCell>
                <TableCell className="font-semibold">{p.data_limit || "unlimited"}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{rupiah(p.modal)}</TableCell>
                <TableCell className="font-mono font-bold text-emerald-500">{rupiah(p.jual)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Seksi({ no, title, hint, color, children, testid }) {
  return (
    <section className={`rounded-2xl border-l-4 ${color} bg-accent/30 p-3 sm:p-4 space-y-3`} data-testid={testid}>
      <div className="flex items-start gap-2.5">
        <span className="shrink-0 w-6 h-6 rounded-full bg-background border border-border text-xs font-bold flex items-center justify-center">{no}</span>
        <div><div className="font-heading font-bold text-sm">{title}</div>{hint && <div className="text-[11px] text-muted-foreground leading-snug">{hint}</div>}</div>
      </div>
      {children}
    </section>
  );
}

const Ket = ({ children }) => <p className="text-[11px] text-muted-foreground leading-snug">{children}</p>;
const INPUT = "bg-background border-2 border-border focus-visible:border-sky-500";

const PHOTO_KINDS = { teknisi: "Foto Teknisi & Tuan Rumah", rumah: "Foto Rumah untuk Server Fasnet", lokasi: "Foto Lokasi", perjanjian: "Perjanjian Tertanda" };

function FotoPicker({ kind, label, hint, file, onPick, testid }) {
  const [preview, setPreview] = useState("");
  useEffect(() => { if (!file) { setPreview(""); return; } const u = URL.createObjectURL(file); setPreview(u); return () => URL.revokeObjectURL(u); }, [file]);
  return (
    <label className={`block rounded-xl border-2 border-dashed ${file ? "border-emerald-500 bg-emerald-500/5" : "border-border bg-background"} p-2 cursor-pointer hover:border-sky-500`} data-testid={testid}>
      <div className="text-xs font-semibold flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-sky-500" /> {label}</div>
      <Ket>{hint}</Ket>
      {preview ? <img src={preview} alt={label} className="mt-2 w-full h-28 object-cover rounded-lg" /> : <div className="mt-2 h-20 rounded-lg bg-accent/60 flex items-center justify-center text-[11px] text-muted-foreground">Ketuk untuk ambil / pilih foto</div>}
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ""; }} data-testid={`${testid}-input`} />
    </label>
  );
}

const uploadPhoto = async (id, raw, kind) => {
  const f = await compressImage(raw);
  const fd = new FormData(); fd.append("file", f, `${kind}.jpg`);
  await api.post(`/branch-requests/${id}/photos?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
};

function Photos({ r, canAdd, onChange }) {
  const [busy, setBusy] = useState(false);
  const photos = r.photos || [];
  const n = photos.length;
  const nPerj = photos.filter((p) => p.kind === "perjanjian").length;
  const upload = (kind) => async (e) => {
    const files = [...e.target.files].slice(0, kind === "perjanjian" ? 3 - nPerj : 9 - n);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const raw of files) await uploadPhoto(r.id, raw, kind);
      toast.success(kind === "perjanjian" ? "Perjanjian tertanda terkirim — menunggu verifikasi admin" : "Foto terkirim"); onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-start gap-2" data-testid={`cabang-photos-${r.id}`}>
      {photos.map((p) => (
        <a key={p.idx} href={`${api.defaults.baseURL}/branch-requests/${r.id}/photos/${p.idx}`} target="_blank" rel="noreferrer" className="w-20 text-center" data-testid={`cabang-photo-${r.id}-${p.idx}`}>
          <img src={`${api.defaults.baseURL}/branch-requests/${r.id}/photos/${p.idx}`} alt={PHOTO_KINDS[p.kind]} className={`w-20 h-20 rounded-lg object-cover border ${p.kind === "perjanjian" ? "border-amber-500" : "border-border"}`} />
          <div className={`text-[9px] leading-tight mt-0.5 ${p.kind === "perjanjian" ? "text-amber-500 font-semibold" : "text-muted-foreground"}`}>{PHOTO_KINDS[p.kind] || p.kind}</div>
        </a>
      ))}
      {canAdd && n < 9 && (
        <label className="w-20 h-20 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-[10px] text-muted-foreground cursor-pointer hover:border-sky-500 hover:text-sky-500" data-testid={`cabang-photo-add-${r.id}`}>
          <Camera className="w-4 h-4 mb-0.5" />{busy ? "..." : "Foto lokasi"}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload("lokasi")} disabled={busy} />
        </label>
      )}
      {canAdd && nPerj < 3 && (
        <label className={`w-20 h-20 rounded-lg border-2 border-dashed ${nPerj ? "border-amber-500/40" : "border-amber-500"} bg-amber-500/10 flex flex-col items-center justify-center text-[10px] text-amber-500 font-semibold text-center leading-tight cursor-pointer hover:bg-amber-500/20`} data-testid={`cabang-photo-perjanjian-${r.id}`}>
          <FileDown className="w-4 h-4 mb-0.5" />{busy ? "..." : nPerj ? "Hal. berikutnya" : "Upload Perjanjian Tertanda"}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload("perjanjian")} disabled={busy} />
        </label>
      )}
      {!n && !canAdd && <span className="text-[11px] text-muted-foreground">Tidak ada foto</span>}
    </div>
  );
}

export default function BukaCabang() {
  const { user } = useAuth();
  const { routers } = useRouters();
  const isAdmin = ["admin", "admin_fasnet"].includes(user?.role);
  const [form, setForm] = useState(EMPTY);
  const [profiles, setProfiles] = useState(null);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [act, setAct] = useState(null); // { id, kind, note, router_id }
  const [setupReq, setSetupReq] = useState(null);
  const [delReq, setDelReq] = useState(null);
  const hapus = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/branch-requests/${delReq.id}`);
      toast.success(data.removed_router ? `Pengajuan ${delReq.name} & cabang otomatisnya dihapus` : `Pengajuan ${delReq.name} dihapus`);
      setDelReq(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const [fotoTeknisi, setFotoTeknisi] = useState(null);
  const [fotoRumah, setFotoRumah] = useState(null);
  const [gps, setGps] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const saveLoc = async (la, ln) => {
    try {
      await api.put(`/branch-requests/${editLoc.id}/location`, { lat: Number(la), lng: Number(ln) });
      toast.success(`Titik rumah server ${editLoc.name} diperbarui`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const ambilGps = () => {
    if (!navigator.geolocation) return toast.error("Perangkat tidak mendukung GPS");
    setGps(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) })); setGps(false); toast.success("Titik koordinat terisi"); },
      () => { setGps(false); toast.error("Gagal mengambil lokasi — izinkan akses lokasi atau isi manual"); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  const load = () => api.get("/branch-requests").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/branch-requests/profiles").then((r) => setProfiles(r.data)).catch(() => {}); load(); }, []);
  const rumahan = form.wifi_type.includes("Rumahan");

  const submit = async (e) => {
    e.preventDefault();
    if (rumahan && Number(form.homes) < 30) return toast.error("Minimal pengajuan pemasangan 30 rumah");
    if (!fotoTeknisi || !fotoRumah) return toast.error("Lampirkan Foto Teknisi & Tuan Rumah dan Foto Rumah untuk Server");
    setBusy(true);
    try {
      const { data: created } = await api.post("/branch-requests", { ...form, homes: Number(form.homes) || 0, pln: form.pln === "ya", lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng) });
      try {
        await uploadPhoto(created.id, fotoTeknisi, "teknisi");
        await uploadPhoto(created.id, fotoRumah, "rumah");
        toast.success("Pengajuan & foto terkirim — unduh PDF perjanjian di Pengajuan Saya");
      } catch (e2) { toast.error("Pengajuan terkirim, tapi foto gagal: " + errMsg(e2)); }
      setForm(EMPTY); setFotoTeknisi(null); setFotoRumah(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const doAct = async () => {
    setBusy(true);
    try {
      await api.put(`/branch-requests/${act.id}/${act.kind}`, { note: act.note, router_id: act.router_id || "" });
      toast.success(act.kind === "approve" ? "Pengajuan disetujui" : "Pengajuan ditolak"); setAct(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="buka-cabang-page">
      {editLoc && <MapPointPicker open lat={editLoc.lat ?? ""} lng={editLoc.lng ?? ""} onClose={() => setEditLoc(null)} onPick={saveLoc} />}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Building2 className="w-6 h-6 text-sky-500" /> Buka Cabang Baru</h1>
        <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "Pengajuan cabang baru dari agen." : "Ajukan pembukaan cabang WiFi baru. Profile & harga standar langsung tersedia saat cabang disetujui admin."}</p>
      </div>

      {!isAdmin && (
        <Card className="bg-card border-border overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-sky-500 via-emerald-500 to-amber-500" />
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Form Pengajuan</CardTitle><Ket>Isi kolom putih di bawah. Teks abu-abu kecil adalah keterangan/petunjuk, bukan isian.</Ket></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <Seksi no="1" title="Identitas Cabang" hint="Nama cabang akan dipakai sebagai nama router & tampil di aplikasi." color="border-sky-500" testid="seksi-identitas">
                <div className="space-y-1.5"><Label>Nama Cabang</Label><Input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Isi nama cabang yang diajukan" data-testid="cabang-name" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label>Tipe WiFi</Label>
                    <Select value={form.wifi_type} onValueChange={(v) => setForm({ ...form, wifi_type: v })}>
                      <SelectTrigger className={INPUT} data-testid="cabang-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`cabang-type-${t.replace(/\W+/g, "-")}`}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Sistem Jaringan</Label>
                    <Select value={form.network} onValueChange={(v) => setForm({ ...form, network: v })}>
                      <SelectTrigger className={INPUT} data-testid="cabang-network"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="OLT">OLT (fiber optik)</SelectItem><SelectItem value="HTB">HTB (kabel LAN/wireless)</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </Seksi>

              {profiles && (
                <Seksi no="2" title="Profile & Harga Standar" hint={`Otomatis tersedia saat cabang ${form.name.trim().toUpperCase() || "baru"} disetujui admin — tidak perlu diisi.`} color="border-emerald-500" testid="seksi-profile">
                  {form.wifi_type.includes("Voucheran") && <ProfilList title="Profile Voucher" items={profiles.voucheran} testid="cabang-profil-voucheran" kind={isAdmin ? "voucher" : "voucher-simple"} showBelum={isAdmin} />}
                  {rumahan && <ProfilList title="Profile WiFi Rumahan" items={profiles.rumahan} testid="cabang-profil-rumahan" kind="rumahan" showBelum={isAdmin} />}
                </Seksi>
              )}

              <Seksi no="3" title="Kondisi Lokasi" hint="Data survei untuk admin menilai kelayakan lokasi." color="border-amber-500" testid="seksi-lokasi">
                <div className="grid grid-cols-2 gap-2">
                  {rumahan && <div className="space-y-1.5"><Label>Jumlah Rumah</Label><Input className={INPUT} type="number" min="30" value={form.homes} onChange={(e) => setForm({ ...form, homes: e.target.value })} required data-testid="cabang-homes" /><Ket>Minimal 30 rumah calon pelanggan.</Ket></div>}
                  <div className="space-y-1.5"><Label>Listrik PLN</Label>
                    <Select value={form.pln} onValueChange={(v) => setForm({ ...form, pln: v })}>
                      <SelectTrigger className={INPUT} data-testid="cabang-pln"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ya">Ya, tersedia</SelectItem><SelectItem value="tidak">Tidak ada</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label>Pesaing</Label><Input className={INPUT} value={form.competitors} onChange={(e) => setForm({ ...form, competitors: e.target.value })} data-testid="cabang-competitors" /><Ket>Nama provider lain di lokasi, mis. Indihome.</Ket></div>
                  <div className="space-y-1.5"><Label>Harga Pesaing</Label><Input className={INPUT} value={form.competitor_price} onChange={(e) => setForm({ ...form, competitor_price: e.target.value })} data-testid="cabang-competitor-price" /><Ket>Mis. Rp 250.000/bulan.</Ket></div>
                </div>
                <div className="space-y-1.5"><Label>Target Pemasangan</Label><DatePicker className={INPUT} value={form.target_date} min={new Date().toISOString().slice(0, 10)} onChange={(v) => setForm({ ...form, target_date: v })} testid="cabang-target" /><Ket>Perkiraan tanggal mulai pemasangan.</Ket></div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={() => setMapOpen(true)} className={`h-12 text-sm font-bold text-white ${form.lat ? "bg-sky-700 hover:bg-sky-800" : "bg-sky-500 hover:bg-sky-600"}`} data-testid="cabang-map-pick">
                    <Map className="w-5 h-5 mr-2" /> Pilih di Peta
                  </Button>
                  <Button type="button" onClick={ambilGps} disabled={gps} className={`h-12 text-sm font-bold text-white ${form.lat ? "bg-emerald-700 hover:bg-emerald-800" : "bg-emerald-500 hover:bg-emerald-600"}`} data-testid="cabang-gps">
                    <MapPin className="w-5 h-5 mr-2" />{gps ? "Mengambil..." : "GPS Lokasi Saya"}
                  </Button>
                </div>
                {form.lat && <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-mono text-emerald-600 dark:text-emerald-300 flex items-center gap-2" data-testid="cabang-coords"><MapPin className="w-4 h-4 shrink-0" /> Titik Rumah Server tersimpan ✓ {form.lat}, {form.lng}</div>}
                <Ket>Geser peta dan letakkan penanda tepat di rumah server — tidak harus berada di lokasi. Atau pakai GPS bila sedang di tempat.</Ket>
                <MapPointPicker open={mapOpen} lat={form.lat} lng={form.lng} onClose={() => setMapOpen(false)} onPick={(la, ln) => { setForm((f) => ({ ...f, lat: la, lng: ln })); toast.success("Titik Rumah Server ditentukan dari peta"); }} />
              </Seksi>

              <Seksi no="4" title="Foto Wajib" hint="Dua foto berikut wajib dilampirkan sebelum mengajukan." color="border-rose-500" testid="seksi-foto">
                <div className="grid grid-cols-2 gap-2">
                  <FotoPicker kind="teknisi" label="Foto Teknisi & Tuan Rumah" hint="Teknisi berfoto bersama tuan rumah di lokasi." file={fotoTeknisi} onPick={setFotoTeknisi} testid="cabang-foto-teknisi" />
                  <FotoPicker kind="rumah" label="Foto Rumah untuk Server" hint="Tampak rumah tempat server Fasnet dipasang." file={fotoRumah} onPick={setFotoRumah} testid="cabang-foto-rumah" />
                </div>
              </Seksi>

              <Seksi no="5" title="Catatan Tambahan" color="border-violet-500" testid="seksi-catatan">
                <Input className={INPUT} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Opsional" data-testid="cabang-note" />
              </Seksi>

              <div className="rounded-2xl border-2 border-amber-500/60 bg-amber-500/10 p-3 sm:p-4 space-y-2" data-testid="catatan-penting">
                <div className="font-heading font-bold text-sm text-amber-500 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Catatan Penting</div>
                <p className="text-xs leading-relaxed">{profiles?.catatan_penting}</p>
                <p className="text-xs leading-relaxed">Setelah pengajuan terkirim, <b>unduh PDF Perjanjian Pemasangan Alat Server PT. Fasnet Sky Fiber</b> di daftar Pengajuan Saya, lalu tanda tangani oleh <b>Teknisi</b> dan <b>TUAN-RUMAH</b>.</p>
              </div>

              <Button type="submit" className="w-full h-12 text-base font-bold bg-sky-600 hover:bg-sky-700 text-white" disabled={busy} data-testid="cabang-submit">{busy ? "Mengirim..." : "Ajukan Cabang Baru"}</Button>
              <Ket>Foto lokasi tambahan bisa dilampirkan di daftar Pengajuan Saya.</Ket>
            </form>
          </CardContent>
        </Card>
      )}

      <BranchLeaderboard />
      {setupReq && <SetupRouterDialog open onClose={() => setSetupReq(null)} request={setupReq} onDone={load} />}
      <Dialog open={!!delReq} onOpenChange={(v) => !v && setDelReq(null)}>
        <DialogContent className="max-w-sm" data-testid="cabang-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Hapus pengajuan {delReq?.name}?</DialogTitle>
            <DialogDescription>Pengajuan akan hilang dari sistem admin <b>dan</b> sisi agen ({delReq?.agent_name || delReq?.agent_email}). {delReq?.status === "approved" && !delReq?.router_setup_at ? "Cabang yang dibuat otomatis (belum di-setup router) beserta profile-nya ikut dihapus." : ""} Foto terlampir dihapus. Tindakan ini tidak bisa dibatalkan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setDelReq(null)} data-testid="cabang-delete-cancel">Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy} onClick={hapus} data-testid="cabang-delete-confirm">{busy ? "Menghapus..." : "Ya, Hapus"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">{isAdmin ? "Semua Pengajuan" : "Pengajuan Saya"}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {list === null && <FibiLoading text="FIBI sedang memuat data..." size={20} />}
          {list?.length === 0 && <p className="text-sm text-muted-foreground text-center py-4" data-testid="cabang-empty">Belum ada pengajuan</p>}
          {list?.map((r) => (
            <div key={r.id} className="rounded-xl border border-border p-3 text-sm space-y-1.5" data-testid={`cabang-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-heading font-bold">{r.name} <span className="text-xs text-muted-foreground font-normal font-mono">• {r.wifi_type} • {r.network}</span></div>
                <span className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border ${STATUS[r.status]}`} data-testid={`cabang-status-${r.id}`}>{r.status === "pending" ? "Menunggu" : r.status === "approved" ? "Disetujui" : "Ditolak"}</span>
                  {user?.role === "admin" && <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-400 hover:bg-rose-500/10" title="Hapus pengajuan (ikut terhapus di sisi agen)" onClick={() => setDelReq(r)} data-testid={`cabang-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </span>
              </div>
              <div className="text-xs text-muted-foreground font-mono grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
                {isAdmin && <span>Agen: {r.agent_code ? `[${r.agent_code}] ` : ""}{r.agent_name || r.agent_email}</span>}
                {r.homes > 0 && <span>{r.homes} rumah</span>}
                <span>PLN: {r.pln ? "ya" : "tidak"}</span>
                <span>Target: {fmtDate(r.target_date)}</span>
                {r.competitors && <span>Pesaing: {r.competitors}{r.competitor_price ? ` (${r.competitor_price})` : ""}</span>}
                <span>Diajukan {fmtDateTime(r.created_at)}</span>
                {r.admin_note && <span className="col-span-full text-foreground">Catatan admin: {r.admin_note}</span>}
              </div>
              {r.lat != null && r.lng != null && <MiniMap lat={r.lat} lng={r.lng} testid={`cabang-minimap-${r.id}`} />}
              {(r.status === "pending" || isAdmin) && (
                <Button size="sm" variant="outline" className="text-sky-500 border-sky-500/40 hover:bg-sky-500/10" onClick={() => setEditLoc(r)} data-testid={`cabang-edit-loc-${r.id}`}><Map className="w-3.5 h-3.5 mr-1" /> {r.lat != null ? "Geser Ulang Titik" : "Tentukan Titik di Peta"}</Button>
              )}
              {r.status === "approved" && r.router_id && <div className="text-xs text-emerald-500 font-mono" data-testid={`cabang-router-link-${r.id}`}>Cabang dibuat: <b>{r.name}</b>{isAdmin ? (r.router_setup_at ? " — MikroTik terhubung, profile sudah di router ✓" : " — profile tersedia (belum di router sampai MikroTik di-setup)") : " — profile & harga sudah tersedia"}</div>}
              {isAdmin && r.status === "approved" && r.router_id && !r.router_setup_at && (
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => setSetupReq(r)} data-testid={`cabang-setup-router-${r.id}`}><Server className="w-3.5 h-3.5 mr-1" /> Isi Host & Login → Buat di Router</Button>
              )}
              {r.status === "pending" && !(r.photos || []).some((p) => p.kind === "perjanjian") && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-500" data-testid={`cabang-perjanjian-missing-${r.id}`}>
                  {isAdmin ? "Belum ada foto perjanjian tertanda — pengajuan belum bisa disetujui." : "Langkah selanjutnya: cetak PDF perjanjian, tanda tangani Teknisi & TUAN-RUMAH, lalu Upload Perjanjian Tertanda."}
                </div>
              )}
              <Photos r={r} canAdd={!isAdmin && r.status === "pending"} onChange={load} />
              <a href={`${api.defaults.baseURL}/branch-requests/${r.id}/perjanjian.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-500 hover:underline" data-testid={`cabang-pdf-${r.id}`}><FileDown className="w-4 h-4" /> Download PDF Perjanjian Pemasangan Alat Server</a>
              {isAdmin && r.status === "pending" && (
                act?.id === r.id ? (
                  <div className="space-y-2 pt-1" data-testid={`cabang-act-${r.id}`}>
                    {act.kind === "approve" && <RouterCombobox routers={[...routers].sort((a, b) => a.name.localeCompare(b.name))} value={act.router_id || "ALL"} onChange={(v) => setAct({ ...act, router_id: v === "ALL" ? "" : v })} allLabel={`Buat cabang baru "${r.name}" otomatis`} testid="cabang-router" />}
                    <Input value={act.note} onChange={(e) => setAct({ ...act, note: e.target.value })} placeholder="Catatan untuk agen (opsional)" data-testid="cabang-act-note" />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1" disabled={busy} onClick={doAct} data-testid="cabang-act-confirm">{act.kind === "approve" ? "Konfirmasi Setujui" : "Konfirmasi Tolak"}</Button>
                      <Button size="sm" variant="outline" onClick={() => setAct(null)} data-testid="cabang-act-cancel">Batal</Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{act.kind === "approve" && act.router_id ? "Profile standar dibuat di router terpilih dan agen ditautkan." : act.kind === "approve" ? `Cabang "${r.name}" langsung dibuat di Daftar Cabang beserta profile standar (belum di router) dan agen ditautkan. Host/login MikroTik diisi admin nanti di Daftar Cabang.` : ""}</p>
                  </div>
                ) : (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!(r.photos || []).some((p) => p.kind === "perjanjian")} title={!(r.photos || []).some((p) => p.kind === "perjanjian") ? "Menunggu foto perjanjian tertanda dari agen" : ""} onClick={() => setAct({ id: r.id, kind: "approve", note: "", router_id: "" })} data-testid={`cabang-approve-${r.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Setujui</Button>
                    <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/40" onClick={() => setAct({ id: r.id, kind: "reject", note: "" })} data-testid={`cabang-reject-${r.id}`}><X className="w-3.5 h-3.5 mr-1" /> Tolak</Button>
                  </div>
                )
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
